import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AssignmentResponsePage from '@/pages/AssignmentResponsePage';
import DashboardLayout from '@/components/DashboardLayout';
import Dashboard from '@/pages/Dashboard';
import TeacherPortal from '@/pages/TeacherPortal';
import AbsencesPage from '@/pages/AbsencesPage';
import ReliefPage from '@/pages/ReliefPage';
import ReliefTodayPage from '@/pages/ReliefTodayPage';
import UsersPage from '@/pages/UsersPage';
import JobTitlesPage from '@/pages/JobTitlesPage';
import TimetablesPage from '@/pages/TimetablesPage';
import ExamInvigilationPage from '@/pages/ExamInvigilationPage';
import ReportsPage from '@/pages/ReportsPage';
import ConfigurationPage from '@/pages/ConfigurationPage';
import CompliancePage from '@/pages/CompliancePage';
import { Toaster } from '@/components/ui/sonner';
import { Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { Brand } from '@/components/ui/Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

const SmartDashboard = () => {
  const { user } = React.useContext(AuthContext);
  if (user?.role === 'teacher') return <TeacherPortal />;
  return <Dashboard />;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
  const WARNING_BEFORE = 2 * 60 * 1000;
  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (!token) return;
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => {
      toast.warning('Your session will expire in 2 minutes. Move the mouse or press a key to stay signed in.', {
        duration: 10000,
      });
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);
    inactivityTimer.current = setTimeout(() => {
      toast.error('Session expired due to inactivity. Please log in again.');
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [token, resetInactivityTimer]);

  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!localStorage.getItem('pwa-install-dismissed')) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setInstallPrompt(null);
  }, [installPrompt]);

  const dismissInstall = useCallback(() => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/dashboard" />} />
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/dashboard" />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/r/response" element={<AssignmentResponsePage />} />
          
          <Route element={user ? <DashboardLayout /> : <Navigate to="/login" />}>
            <Route path="/dashboard" element={<SmartDashboard />} />
            <Route path="/absences" element={<AbsencesPage />} />
            <Route path="/relief" element={<ReliefPage />} />
            <Route path="/relief/today" element={<ReliefTodayPage />} />
            <Route path="/timetables" element={<TimetablesPage />} />
            <Route path="/exams" element={<ExamInvigilationPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/job-titles" element={<JobTitlesPage />} />
            <Route path="/configuration" element={<ConfigurationPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
          </Route>
          
          <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      {showInstallBanner && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-slate-900 text-white rounded-xl shadow-2xl p-4 z-50 flex items-center gap-3 animate-in slide-in-from-bottom-4" data-testid="pwa-install-banner">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <Download className="h-5 w-5 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Install <Brand theme="dark" /></p>
            <p className="text-xs text-slate-400">Add to your home screen for quick access</p>
          </div>
          <button onClick={handleInstall} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors" data-testid="pwa-install-button">
            Install
          </button>
          <button onClick={dismissInstall} className="p-1 hover:bg-slate-800 rounded-lg transition-colors" data-testid="pwa-dismiss-button">
            <X className="h-4 w-4 text-slate-400" />
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export default App;
